import React, { useContext, useMemo, useRef } from "react";
import { AppContext } from "../../contextProviders/AppContext";
import { Outlet } from "react-router-dom";
import {
  Grid,
  Typography,
  useTheme,
  Container,
  Box,
  Stack,
  Divider,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import { getImgHostingUrl } from "../../utilities";

import { translation } from "../../components/Dictionary";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import fiFlag from "/flags/fi.png";
import seFlag from "/flags/se.png";
import enFlag from "/flags/en.png";
export default function StepperComponent() {
  const theme = useTheme();
  const container = useRef();
  const {
    activeStep,
    handleActiveStep,
    mobileViewPort,
    language,
    setLanguage,
    setInitSearch,
    enableCustomProviderLabel,
  } = useContext(AppContext);

  // Change steps label depending on company
  const DEPENDENCY_CHANGE_STEP1_LABEL = useMemo(() => {
    if (enableCustomProviderLabel())
      return translation.get(language, "Step 1 custom label");
    return translation.get(language, "Step 1 default label");
  }, [enableCustomProviderLabel, language]);

  const DEPENDENCY_CHANGE_STEP2_LABEL = useMemo(() => {
    if (enableCustomProviderLabel())
      translation.get(language, "Step 2 custom label");
    return translation.get(language, "Step 2 default label");
  }, [enableCustomProviderLabel, language]);

  // Define directions for the step
  const steps = [
    DEPENDENCY_CHANGE_STEP1_LABEL,
    DEPENDENCY_CHANGE_STEP2_LABEL,
    translation.get(language, "Step 3 default label"),
    translation.get(language, "Step 4 default label"),
  ];

  // Navigate by
  const changeStep = (value) => {
    if (value > activeStep) return null;
    if (activeStep > 2 && value === 2) {
      setInitSearch(true);
      handleActiveStep(value);
    } else {
      handleActiveStep(value);
    }
  };

  const dividerMargin = {
    marginLeft: mobileViewPort ? "0px" : "5px",
    marginRight: mobileViewPort ? "0px" : "5px",
  };

  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const open = Boolean(menuAnchor);
  const handleClick = (event) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleClose = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          backgroundColor: theme.palette.primary.background,

          padding: 0,
          paddingBottom: mobileViewPort ? 0 : "45px",
        }}
        ref={container}
        data-cy="stepContainer"
        disableGutters
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            paddingBottom: "15px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginRight: "15px",
            }}
          >
            <IconButton onClick={handleClick} size="small">
              <Avatar
                sx={{ boxShadow: theme.shadows[4] }}
                src={
                  availableLanguages.find((lang) => lang.value === language)
                    ?.flag
                }
              ></Avatar>
            </IconButton>
            <Typography variant="caption">
              <LanguageTitle language={language} />
            </Typography>
          </Box>
          <Menu
            anchorEl={menuAnchor}
            id="select-language-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{ style: { borderRadius: "10px" } }}
          >
            {availableLanguages.map((lang) => {
              return (
                <MenuItem
                  onClick={() => setLanguage(lang.value)}
                  key={lang.value}
                >
                  {lang.title}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
        <Grid
          item
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-evenly",
            marginTop: mobileViewPort ? "0px" : "25px",
            bgcolor: theme.palette.primary.stepperBg,
          }}
          container
        >
          <MobileStepper
            changeStep={changeStep}
            activeStep={activeStep}
            steps={steps}
            visibility={mobileViewPort}
            value={activeStep * 25}
          />

          <Stepper
            changeStep={changeStep}
            activeStep={activeStep}
            steps={steps}
            visibility={!mobileViewPort}
          />
        </Grid>
      </Container>
      <Outlet />
    </>
  );
}

let env =
  process.env.REACT_APP_ENV === "local" ? "dev2" : process.env.REACT_APP_ENV;

const availableLanguages = [
  {
    title: "FI",
    value: "fi",
    flag: getImgHostingUrl(fiFlag),
  },
  {
    title: "SE",
    value: "se",
    flag: getImgHostingUrl(seFlag),
  },
  {
    title: "EN",
    value: "en",
    flag: getImgHostingUrl(enFlag),
  },
];

function LanguageTitle({ language }) {
  return availableLanguages.find((lang) => lang.value === language).title;
}
function Stepper(props) {
  const { changeStep, activeStep, steps, visibility } = props;
  const { mobileViewPort } = useContext(AppContext);
  const theme = useTheme();

  if (visibility)
    return steps.map((label, index) => (
      <Tooltip
        key={index}
        title={index + 1 < activeStep ? `Palaa vaiheeseen: ${label}` : ""}
      >
        <Grid
          item
          xs={3}
          key={label}
          sx={{
            bgcolor:
              index + 1 === activeStep && theme.palette.primary.stepperBgActive,

            transition: "250ms ease-in-out",
            position: "relative",
            justifyContent: "center",
            alignItems: "center",
          }}
          container
        >
          {index !== 3 && <span style={{ flex: 1 }} />}

          <Stack
            onClick={() => changeStep(index + 1)}
            direction={"column"}
            justifyContent="center"
            alignItems="center"
            sx={{
              padding: mobileViewPort ? "0px" : "10px",

              /*          width: mobileViewPort ? "80px" : undefined,
      borderStyle: "solid",
      borderWidth: 2,
      borderColor: "transparent",
      borderRadius: "10px", */
              "&:hover": {
                cursor: index < activeStep ? "pointer" : "undefined",
                borderColor:
                  index + 1 < activeStep
                    ? theme.palette.primary.main
                    : "transparent",
              },
            }}
          >
            <Avatar
              sx={{
                height: "26px",
                width: "26px",
                //   bgcolor: "transparent",
                bgcolor:
                  index + 1 === activeStep
                    ? theme.palette.primary.main
                    : index < activeStep
                    ? theme.palette.primary.stepperCompleted
                    : theme.palette.primary.stepperInactiveAvatar,
                transition: "250ms ease-in-out",
                borderStyle: "solid",
                borderWidth: 2,
                borderColor:
                  index + 1 === activeStep
                    ? theme.palette.primary.main
                    : index < activeStep
                    ? "lightgrey"
                    : "lightgrey",
                fontWeight: index + 1 === activeStep ? "bolder" : "normal",
              }}
            >
              {index + 1 < activeStep ? (
                <Check
                  sx={{
                    fontSize: "18px",
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    color:
                      index + 1 === activeStep
                        ? theme.palette.primary.stepperActiveValue
                        : theme.palette.primary.stepperInactiveValue,
                  }}
                >
                  <b> {index + 1} </b>
                </Typography>
              )}
            </Avatar>
            <Typography
              sx={{
                marginTop: "5px",
                textAlign: mobileViewPort ? "center" : undefined,
                fontFamily: "Sofia Pro",
                wordWrap: index + 1 === activeStep ? "break-word" : undefined,
                fontSize: mobileViewPort
                  ? index + 1 === activeStep
                    ? "14px"
                    : "12px"
                  : "16px",
                color:
                  index + 1 === activeStep
                    ? theme.palette.primary.stepperActiveText
                    : theme.palette.primary.stepperInactiveText,
              }}
            >
              {label}
            </Typography>
          </Stack>

          {index !== 3 && (
            <>
              <span style={{ flex: 1 }} />
              <Divider
                key={label}
                variant="middle"
                orientation="vertical"
                flexItem
              />
            </>
          )}
        </Grid>
      </Tooltip>
    ));
}

function MobileStepper(props) {
  const { changeStep, activeStep, steps, visibility, value } = props;

  const theme = useTheme();

  function handleStepBack() {
    if (activeStep > 0) {
      changeStep(activeStep - 1);
    }
  }
  if (visibility)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex§-start",
          alignItems: "center",
          width: "100%",
        }}
      >
        <IconButton
          onClick={() => handleStepBack()}
          disabled={activeStep === 1}
        >
          <ChevronLeftIcon
            sx={{
              fontSize: "32px",
              color: theme.palette.primary.mobileStepper,
            }}
          />
        </IconButton>
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            padding: "10px",
            color: theme.palette.primary.mobileStepperProgress,
          }}
        >
          <CircularProgress
            variant="determinate"
            color="inherit"
            value={value}
            size={60}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.mobileStepper,
                fontSize: "18px",
              }}
            >
              {`${activeStep}  / ${steps.length}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", marginLeft: "10px" }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.primary.mobileStepper,
              fontSize: "22px",
            }}
          >
            {steps[activeStep - 1]}
          </Typography>
        </Box>
      </Box>
    );
}
